import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { Edit2, Trash2, X, Search } from "lucide-react";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";

const AdminBlogComments = () => {
    const [comments, setComments] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");

    // 🔹 Modal states
    const [showModal, setShowModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState(null);

    const url = API_BASE_URL
    const token = decryptToken("accessToken") || ""
    // 🔹 Fetch all comments
    const fetchComments = async () => {
        try {
            const res = await fetch(`${url}/blog-comment/all/adminnew1950dr`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const data = await res.json();
            const list = Array.isArray(data.data) ? data.data : [];
            setComments(list);
            setFiltered(list);
        } catch {
            toast.error("Failed to load comments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, []);

    // 🔍 Search filter
    useEffect(() => {
        const s = search.toLowerCase();
        setFiltered(
            comments.filter(
                (c) =>
                    c.author?.toLowerCase().includes(s) ||
                    c.email?.toLowerCase().includes(s) ||
                    c.message?.toLowerCase().includes(s) ||
                    c.blog_id?.toLowerCase().includes(s)
            )
        );
    }, [search, comments]);

    // 🪟 Open modal
    const openEditModal = (comment) => {
        setSelectedComment({ ...comment });
        setShowModal(true);
    };

    // ❌ Close modal
    const closeModal = () => {
        setShowModal(false);
        setSelectedComment(null);
    };

    // ✏️ Update comment (ALL fields)
    const handleUpdate = async () => {
        try {
            const res = await fetch(`${url}/blog-comment/${selectedComment.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    author: selectedComment.author,
                    email: selectedComment.email,
                    blog_id: selectedComment.blog_id,
                    message: selectedComment.message,
                }),
            });

            if (!res.ok) throw new Error();

            toast.success("Comment updated successfully");
            closeModal();
            fetchComments();
        } catch {
            toast.error("Update failed");
        }
    };

    // 🗑️ Delete comment
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this comment permanently?")) return;

        try {
            const res = await fetch(`${url}/blog-comment/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error();

            toast.success("Comment deleted");
            setComments((prev) => prev.filter((c) => c.id !== id));
        } catch {
            toast.error("Delete failed");
        }
    };

    const toggleBlogStatus = async (blogId, status) => {
        try {
            const nextStatus = status === "Active" ? "Inactive" : "Active";

            const response = await fetch(`${url}/blog-comment/status/${blogId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ status: nextStatus })
            });

            const resData = await response.json();

            if (response.ok) {
                toast.success(resData.message);
                setComments(prev =>
                    prev.map(b => b.id === blogId ? { ...b, status: nextStatus } : b)
                );
            } else {
                toast.error(resData.message);
            }

        } catch (err) {
            toast.error("Something went wrong");
        }
    };


    if (loading) {
        return <div className="p-6 text-center">Loading comments...</div>;
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h2 className="text-2xl font-bold mb-4">All Blog Comments</h2>

            {/* 🔍 Search */}
            <div className="relative w-72 mb-4">
                <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search comments..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border rounded text-sm"
                />
            </div>

            {/* 📋 Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#f9f9f9] text-gray-900">
                        <tr>
                            <th className="p-3 text-left">Author</th>
                            <th className="p-3 text-left">Email</th>
                            <th className="p-3 text-left">Blog ID</th>
                            <th className="p-3 text-left">Comment</th>
                            <th className="p-3">Date</th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="p-4 text-center">
                                    No comments found
                                </td>
                            </tr>
                        ) : (
                            filtered.map((comment) => (
                                <tr key={comment.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3">{comment.author}</td>
                                    <td className="p-3 text-gray-600">{comment.email}</td>
                                    <td className="p-3 text-gray-600">{comment.blog_id}</td>
                                    <td className="p-3 max-w-md truncate">{comment.message}</td>
                                    <td className="p-3 text-gray-600">
                                        {new Date(comment.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="p-3 text-center">
                                        <label
                                            className="relative inline-flex items-center cursor-pointer"
                                            title={
                                                comment.status === "Active"
                                                    ? "Click to move to Draft"
                                                    : "Click to Publish"
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={comment.status === "Active"}
                                                onChange={() => toggleBlogStatus(comment.id, comment.status)}
                                            />

                                            <div
                                                className={`w-10 h-5 rounded-full transition-colors duration-200 relative ${comment.status === "Active" ? "bg-green-500" : "bg-gray-400"
                                                    }`}
                                            >
                                                <div
                                                    className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 absolute top-0.5 left-0.5 ${comment.status === "Active" ? "translate-x-5" : "translate-x-0"
                                                        }`}
                                                ></div>
                                            </div>
                                        </label>
                                    </td>
                                    <td className="p-3 text-center">
                                        <div className="flex justify-center gap-3">
                                            <button
                                                onClick={() => openEditModal(comment)}
                                                className="text-blue-600"
                                            >
                                                <Edit2 size={16} />
                                            </button>

                                            <button
                                                onClick={() => handleDelete(comment.id)}
                                                className="text-red-600"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 🪟 EDIT MODAL */}
            {showModal && selectedComment && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg w-full max-w-lg p-6 relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                        >
                            <X size={20} />
                        </button>

                        <h3 className="text-lg font-semibold mb-4">Edit Comment</h3>

                        <div className="space-y-3">
                            <input
                                type="text"
                                value={selectedComment.author}
                                onChange={(e) =>
                                    setSelectedComment({
                                        ...selectedComment,
                                        author: e.target.value,
                                    })
                                }
                                placeholder="Author"
                                className="w-full border rounded p-2 text-sm"
                            />

                            <input
                                type="email"
                                value={selectedComment.email}
                                onChange={(e) =>
                                    setSelectedComment({
                                        ...selectedComment,
                                        email: e.target.value,
                                    })
                                }
                                placeholder="Email"
                                className="w-full border rounded p-2 text-sm"
                            />

                            <input
                                type="text"
                                value={selectedComment.blog_id}
                                onChange={(e) =>
                                    setSelectedComment({
                                        ...selectedComment,
                                        blog_id: e.target.value,
                                    })
                                }
                                placeholder="Blog ID"
                                disabled
                                className="w-full border rounded p-2 text-sm"
                            />

                            <textarea
                                rows="4"
                                value={selectedComment.message}
                                onChange={(e) =>
                                    setSelectedComment({
                                        ...selectedComment,
                                        message: e.target.value,
                                    })
                                }
                                placeholder="Comment message"
                                className="w-full border rounded p-2 text-sm"
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-200 rounded text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBlogComments;
